import {
  array,
  boolean as booleanType,
  Describe,
  nullable,
  object,
  optional,
  string,
} from 'superstruct';

import {
  AugmentedCreateCustodialPersonalAccountInput,
  AugmentedCreateEntityInput,
  AugmentedUpdateCustodialPersonalAccountInput,
  AugmentedUpdateEntityInput,
} from '@/types/schema';

import {
  commonCreatePersonalAccountProperties,
  commonUpdatePersonalAccountProperties,
  controllingPartySchema,
} from './personalAccountTypes';
import {
  beneficiarySchema,
  commonUpdateEntityProperties,
  createAccountSchema,
  createEntityInput,
  grantorSchema,
  gstStatusSchema,
  inclusionRatioSchema,
  inEstateStatusSchema,
  nonTrustEntityTaxStatus,
  updateAccountSchema,
} from './types';
import { $getValidatedInput } from './utils';

const createCustodialAccountSchema: Describe<AugmentedCreateCustodialPersonalAccountInput> =
  object({
    create: object({
      ...commonCreatePersonalAccountProperties,
      taxStatus: nonTrustEntityTaxStatus,
      gstStatus: gstStatusSchema,
      inEstateStatus: inEstateStatusSchema,
      stateInEstateStatus: inEstateStatusSchema,
      inclusionRatio: inclusionRatioSchema,
    }),
    withOwner: optional(nullable(grantorSchema)),
    withBeneficiaries: optional(nullable(array(beneficiarySchema))),
    withControllingParties: optional(nullable(array(controllingPartySchema))),
    withDesignerAccount: optional(nullable(createAccountSchema)),
  });

const updateCustodialAccountSchema: Describe<AugmentedUpdateCustodialPersonalAccountInput> =
  object({
    id: string(),
    update: object({
      ...commonUpdatePersonalAccountProperties,
      taxStatus: optional(nullable(nonTrustEntityTaxStatus)),
      gstStatus: gstStatusSchema,
      inEstateStatus: inEstateStatusSchema,
      stateInEstateStatus: inEstateStatusSchema,
      inclusionRatio: inclusionRatioSchema,
      clearControllingParties: optional(nullable(booleanType())),
      clearBeneficiaries: optional(nullable(booleanType())),
      clearOwner: optional(nullable(booleanType())),
      clearDescription: optional(nullable(booleanType())),
      clearGstStatus: optional(nullable(booleanType())),
      clearTaxStatus: optional(nullable(booleanType())),
      clearInclusionRatio: optional(nullable(booleanType())),
    }),
    withOwner: optional(nullable(grantorSchema)),
    withBeneficiaries: optional(nullable(array(beneficiarySchema))),
    withControllingParties: optional(nullable(array(controllingPartySchema))),
    updateDesignerAccount: optional(nullable(updateAccountSchema)),
  });

export const createSchema: Describe<AugmentedCreateEntityInput> = object({
  create: createEntityInput,
  withCustodialPersonalAccount: optional(
    nullable(createCustodialAccountSchema)
  ),
});

export const updateSchema: Describe<AugmentedUpdateEntityInput> = object({
  id: string(),
  update: object(commonUpdateEntityProperties),
  updateCustodialPersonalAccount: optional(
    nullable(updateCustodialAccountSchema)
  ),
});

export function validateCustodialAccountCreateInput(
  input: AugmentedCreateEntityInput
): AugmentedCreateEntityInput {
  return $getValidatedInput(input, createSchema, 'Custodial account create');
}

export function validateCustodialAccountUpdateInput(
  input: AugmentedUpdateEntityInput
): AugmentedUpdateEntityInput {
  return $getValidatedInput(input, updateSchema, 'Custodial account update');
}
